/**
 * Sensor sample shapes + config. Phase 2's SITL plugins consume the same
 * `SensorSample`, so the field set is deliberately close to what a real IMU /
 * GPS / baro / rangefinder report.
 */
import type { Vec3, Quat, Geodetic } from './mathx';

export type GpsFix = 'NO_FIX' | 'FIX_3D';

export interface GpsSample {
  lat: number;
  lon: number;
  alt: number;
  /** velocity in the local ENU frame, m/s */
  vEnu: Vec3;
  fix: GpsFix;
  nSat: number;
}

export interface SensorSample {
  simTime: number;
  /** body-frame angular rate, rad/s */
  gyro: Vec3;
  /** body-frame specific force (what an accelerometer reads): +g up at rest, 0 in free fall, m/s² */
  accel: Vec3;
  /** ground-truth orientation (body→world). Debug / render only — not a real sensor. */
  quatTrue: Quat;
  gps: GpsSample;
  /** barometric altitude above the scenario origin, m */
  baroAlt: number;
  /** height above ground, m, or null when out of range / not level */
  rangefinderAgl: number | null;
  batteryV: number;
}

/** Everything `Sensors.sample` needs from the sim each tick. */
export interface SensorInput {
  simTime: number;
  /** body position in the local ENU frame, m */
  posEnu: Vec3;
  /** body velocity in the local ENU frame, m/s */
  velEnu: Vec3;
  /** orientation body→world */
  quat: Quat;
  /** angular rate in the body frame, rad/s */
  angVelBody: Vec3;
  /**
   * Specific force in the world frame = (sum of non-gravitational forces) / mass.
   * At rest this is (0,0,+g); in free fall it is 0.
   */
  properAccelWorld: Vec3;
  /** height above ground, m */
  agl: number;
  batteryV: number;
}

export interface AxisNoiseConfig {
  /** white-noise standard deviation (units per axis) */
  whiteSigma: number;
  /** bias random-walk standard deviation, per √second */
  biasWalkSigma: number;
}

export interface SensorConfig {
  /** zero all noise, bias and drift — for deterministic controller tests */
  perfect: boolean;
  gyro: AxisNoiseConfig;
  accel: AxisNoiseConfig;
  gps: {
    rateHz: number;
    /** seconds after start before the fix becomes FIX_3D */
    fixDelayS: number;
    horizontalSigmaM: number;
    verticalSigmaM: number;
    velocitySigmaMps: number;
    satsWithFix: number;
  };
  baro: {
    whiteSigmaM: number;
    driftWalkSigmaM: number;
  };
  rangefinder: {
    maxRangeM: number;
    whiteSigmaM: number;
    /** cone half-angle (rad) beyond which the beam misses the ground */
    coneHalfAngleRad: number;
  };
}

export const DEFAULT_SENSOR_CONFIG: SensorConfig = {
  perfect: false,
  gyro: { whiteSigma: 0.02, biasWalkSigma: 0.002 },
  accel: { whiteSigma: 0.2, biasWalkSigma: 0.02 },
  gps: {
    rateHz: 5,
    fixDelayS: 2,
    horizontalSigmaM: 1.5,
    verticalSigmaM: 2.5,
    velocitySigmaMps: 0.15,
    satsWithFix: 12,
  },
  baro: { whiteSigmaM: 0.15, driftWalkSigmaM: 0.05 },
  rangefinder: { maxRangeM: 40, whiteSigmaM: 0.02, coneHalfAngleRad: (25 * Math.PI) / 180 },
};

const isRecord = (x: unknown): x is Record<string, unknown> =>
  typeof x === 'object' && x !== null && !Array.isArray(x);

const nOr = (obj: Record<string, unknown> | undefined, key: string, fallback: number): number => {
  const val = obj?.[key];
  return typeof val === 'number' && Number.isFinite(val) ? val : fallback;
};

/** Permissive parse: every field falls back to {@link DEFAULT_SENSOR_CONFIG}. */
export const parseSensorConfig = (raw: unknown): SensorConfig => {
  if (!isRecord(raw)) return { ...DEFAULT_SENSOR_CONFIG };
  const d = DEFAULT_SENSOR_CONFIG;
  const gyro = isRecord(raw.gyro) ? raw.gyro : undefined;
  const accel = isRecord(raw.accel) ? raw.accel : undefined;
  const gps = isRecord(raw.gps) ? raw.gps : undefined;
  const baro = isRecord(raw.baro) ? raw.baro : undefined;
  const rf = isRecord(raw.rangefinder) ? raw.rangefinder : undefined;
  return {
    perfect: raw.perfect === true,
    gyro: {
      whiteSigma: nOr(gyro, 'whiteSigma', d.gyro.whiteSigma),
      biasWalkSigma: nOr(gyro, 'biasWalkSigma', d.gyro.biasWalkSigma),
    },
    accel: {
      whiteSigma: nOr(accel, 'whiteSigma', d.accel.whiteSigma),
      biasWalkSigma: nOr(accel, 'biasWalkSigma', d.accel.biasWalkSigma),
    },
    gps: {
      rateHz: nOr(gps, 'rateHz', d.gps.rateHz),
      fixDelayS: nOr(gps, 'fixDelayS', d.gps.fixDelayS),
      horizontalSigmaM: nOr(gps, 'horizontalSigmaM', d.gps.horizontalSigmaM),
      verticalSigmaM: nOr(gps, 'verticalSigmaM', d.gps.verticalSigmaM),
      velocitySigmaMps: nOr(gps, 'velocitySigmaMps', d.gps.velocitySigmaMps),
      satsWithFix: nOr(gps, 'satsWithFix', d.gps.satsWithFix),
    },
    baro: {
      whiteSigmaM: nOr(baro, 'whiteSigmaM', d.baro.whiteSigmaM),
      driftWalkSigmaM: nOr(baro, 'driftWalkSigmaM', d.baro.driftWalkSigmaM),
    },
    rangefinder: {
      maxRangeM: nOr(rf, 'maxRangeM', d.rangefinder.maxRangeM),
      whiteSigmaM: nOr(rf, 'whiteSigmaM', d.rangefinder.whiteSigmaM),
      coneHalfAngleRad: nOr(rf, 'coneHalfAngleRad', d.rangefinder.coneHalfAngleRad),
    },
  };
};

export type { Geodetic };
